/* eslint-disable camelcase */

import { Action } from 'redux';
import {
  ApiDataAction,
  ApiFailureAction,
  ApiRequestAction,
  ApiSuccessAction,
  RestaurantBranch,
} from 'lib/types/global';

import { SET_DATA_FILTERS } from '../dataFilters/actionTypes';
import {
  API_CREATE_REPORT_FAILURE,
  API_CREATE_REPORT_REQUEST,
  API_CREATE_REPORT_SUCCESS,
  API_CREATE_REPORT,
  API_GET_REPORT,
  API_GET_REPORTS_FAILURE,
  API_GET_REPORTS_REQUEST,
  API_GET_REPORTS_SUCCESS,
  API_GET_REPORTS,
  NEW_REPORT_CLEAR_SUCCESS_BANNER,
  NEW_REPORT_INIT,
  NEW_REPORT_SET_PERIOD,
  NEW_REPORT_SET_FREQUENCY,
  NEW_REPORT_SET_RECIPIENTS,
  NEW_REPORT_SET_SITES,
  RESET_REPORT,
  REPORTS_PAGINATION_CHANGE_PAGE,
  API_GET_SCHEDULED_REPORTS,
  API_GET_SCHEDULED_REPORTS_REQUEST,
  API_GET_SCHEDULED_REPORTS_SUCCESS,
  API_GET_SCHEDULED_REPORTS_FAILURE,
  RESET_SCHEDULED_REPORT,
  SCHEDULED_REPORTS_CHANGE_PAGE,
  SCHEDULED_REPORTS_CHANGE_FILTER,
  API_DELETE_SCHEDULED_REPORT_REQUEST,
  API_DELETE_SCHEDULED_REPORT_SUCCESS,
  API_DELETE_SCHEDULED_REPORT_FAILURE,
  API_DELETE_SCHEDULED_REPORT,
  DELETE_SCHEDULED_REPORT_SET_LOADING,
  DELETE_SCHEDULED_REPORT_REMOVE_LOADING,
  RESET_REPORT_FREQUENCY,
} from './reducer';

/*
  Types

  If a new type is added, please add the type to the enum in ReportingPlatformClickedEvent in transport-models
  This is necessary for us to track our product

*/

export enum ReportTypes {
  PERFORMANCE = 'performance',
  CUSTOMERS = 'customers',
  SPEED = 'speed',
  AVAILABILITY = 'availability',
  ITEMS_SOLD = 'items_sold',
  ORDERS = 'orders',
}

export enum Frequencies {
  ONCE = 'once',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export enum OrderSources {
  SIGNATURE = 'signature',
  CORE = 'core',
}

export enum TimePeriods {
  WEEK = 'week',
  MONTH = 'month',
  CUSTOM = 'custom',
}

export type ReportStatus =
  | 'available'
  | 'impossible_generate'
  | 'enqueued'
  | 'expired'
  | 'generating';

export enum ReportCreationTypes {
  NEW = 'new',
}

export type Report = {
  drnId: string;
  externalUserId: string;
  startDate: string;
  endDate: string;
  orderSource: OrderSources;
  reportType: ReportTypes;
  restaurantDrnIds: string[];
  requestedAt: string;
  status: ReportStatus;
  scheduledReportId?: string;
  s3Key?: string;
};

export type ScheduledReport = {
  id: number;
  drnId: string;
  drnMarket: string;
  userId: number;
  userDrnId: string;
  restaurantDrnIds: string[];
  orderSource: OrderSources;
  reportType: ReportTypes;
  frequency: Frequencies;
  createdAt: string;
  updatedAt: string;
  scheduledAt: string;
};

export interface RestaurantBranchSelection extends RestaurantBranch {
  selected?: boolean;
}

/*
  State types
*/

export interface ReportingPlatformScheduledReportsFilterState {
  orderSource?: OrderSources;
  reportType?: ReportTypes;
  timePeriod?: TimePeriods;
  restaurantDrnIds?: string[];
}

export interface ReportingPlatformScheduledReportsState {
  reports: ScheduledReport[];
  isLoading: boolean;
  isFirstTimeLoaded: boolean;
  isError?: boolean;
  totalReports?: number;
  currentPage: number;
  filter: ReportingPlatformScheduledReportsFilterState;
  isDeleting: Record<string, boolean>;
  isDeletingError?: boolean;
}

export interface ReportingPlatformNewReportState {
  drnId?: string;
  restaurantDrnIds?: string[];
  reportType?: ReportTypes;
  temporaryId?: string;
  creationType?: ReportCreationTypes;
  countAllReports?: number;
  orderSource?: OrderSources;
  startDate?: string;
  endDate?: string;
  timePeriod?: TimePeriods;
  frequency?: Frequencies;
  recipients?: string[];
  selectedMarket?: string;
}

export interface ReportingPlatformState {
  hasGeneratingReports: boolean;
  reports: Array<Report>;
  newReport?: ReportingPlatformNewReportState;
  isLoading: boolean;
  isFirstTimeLoaded: boolean;
  isError: boolean;
  showNewReportSuccessBanner?: boolean;
  totalReports?: number;
  reportsPagination: {
    currentPage: number;
  };
  scheduledReports: ReportingPlatformScheduledReportsState;
}

/*
  Action parameter types
*/

export type ApiGetReportParams = {
  reportDrnId: string;
};

export type ApiGetReportsParams = {
  restaurantDrnIds?: string[];
  reportType?: ReportTypes;
  orderSource?: OrderSources;
  startingAfter?: number;
  limit?: string;
};

export type ApiGetScheduledReportsParams = {
  restaurantDrnIds?: string[];
  reportType?: ReportTypes;
  orderSource?: OrderSources;
  timePeriod?: TimePeriods;
  offset?: number;
  limit?: string;
};

export type ApiCreateReportParams = {
  drnId?: string;
  restaurantDrnIds: string[];
  restaurantIds?: string[];
  reportType: ReportTypes;
  startDate: string;
  endDate: string;
  frequency: Frequencies;
  orderSource: OrderSources;
  timeZone: string;
};

export type InitialiseNewReportParams = {
  reportType: ReportTypes;
  orderSource: OrderSources;
  temporaryId: string;
  countAllReports?: number;
  creationType: ReportCreationTypes;
  selectedMarket: string;
};

export type SetReportPeriodParams = {
  startDate: string;
  endDate: string;
  timePeriod: TimePeriods;
};

export type SetReportFrequencyParams = {
  frequency: Frequencies;
  startDate?: string;
  endDate?: string;
};

export type SetSitesParams = {
  restaurantDrnIds: string[];
};

export type SetRecipientsParams = {
  recipients: string[];
};

export type SetCurrentPageParams = {
  currentPage: number;
};

export type SetScheduledReportsCurrentPageParams = {
  currentPage: number;
};

export type SetScheduledReportsFilterParams =
  ReportingPlatformScheduledReportsFilterState;

export type DeleteScheduledReportParams = {
  drnId: string;
};

export interface DeleteScheduledReportActionParams {
  drnId: string;
  params: ApiGetScheduledReportsParams;
  currentPage: number;
  reportsCount: number;
}

/*
  API Action request/response types
*/

export type ApiCreateReportAction = ApiDataAction<
  typeof API_CREATE_REPORT,
  {
    restaurantDrnIds: string[];
    restaurantIds?: string[];
    reportType?: ReportTypes;
    orderSource?: OrderSources;
    startDate?: string;
    endDate?: string;
    period?: TimePeriods;
    frequency?: Frequencies;
  }
> & {
  options: {
    method: 'POST' | 'PATCH';
  };
};
export type ApiCreateReportActionRequest = ApiRequestAction<
  typeof API_CREATE_REPORT_REQUEST
>;
export type ApiCreateReportActionSuccess = ApiSuccessAction<
  typeof API_CREATE_REPORT_SUCCESS,
  { statusCode: string }
>;
export type ApiCreateReportActionFailure = ApiFailureAction<
  typeof API_CREATE_REPORT_FAILURE
>;

export type ApiGetReportAction = ApiDataAction<typeof API_GET_REPORT, Report>;

export interface ApiGetReportsPayload {
  restaurant_drn_ids: string[];
  report_type?: ReportTypes;
  order_source?: OrderSources;
  starting_after?: number;
  limit?: string;
}

export type ApiGetReportsAction = ApiDataAction<
  typeof API_GET_REPORTS,
  ApiGetReportsPayload
>;
export type ApiGetReportsActionRequest = ApiRequestAction<
  typeof API_GET_REPORTS_REQUEST
>;
export type ApiGetReportsActionSuccess = ApiSuccessAction<
  typeof API_GET_REPORTS_SUCCESS,
  {
    hasGeneratingReports: boolean;
    reports: Array<Report>;
  }
>;
export type ApiGetReportsActionFailure = ApiFailureAction<
  typeof API_GET_REPORTS_FAILURE
>;

export interface ApiGetScheduledReportsPayload {
  restaurant_drn_ids: string[];
  report_type?: ReportTypes;
  order_source?: OrderSources;
  period?: TimePeriods;
  offset?: number;
  limit?: string;
}

export type ApiGetScheduledReportsAction = ApiDataAction<
  typeof API_GET_SCHEDULED_REPORTS,
  ApiGetScheduledReportsPayload
>;
export type ApiGetScheduledReportsActionRequest = ApiRequestAction<
  typeof API_GET_SCHEDULED_REPORTS_REQUEST
>;
export type ApiGetScheduledReportsActionSuccess = ApiSuccessAction<
  typeof API_GET_SCHEDULED_REPORTS_SUCCESS,
  {
    totalReports: number;
    scheduledReports: ScheduledReport[];
  }
>;
export type ApiGetScheduledReportsActionFailure = ApiFailureAction<
  typeof API_GET_SCHEDULED_REPORTS_FAILURE
>;

export type SetScheduledReportsCurrentPageAction = Action<
  typeof SCHEDULED_REPORTS_CHANGE_PAGE
> &
  SetScheduledReportsCurrentPageParams;

export type SetScheduledReportsFilterAction = Action<
  typeof SCHEDULED_REPORTS_CHANGE_FILTER
> &
  SetScheduledReportsFilterParams;

/*
  Action request/response types
*/

export type InitialiseNewReportAction = Action<typeof NEW_REPORT_INIT> &
  InitialiseNewReportParams;

export type SetReportPeriodAction = Action<typeof NEW_REPORT_SET_PERIOD> &
  SetReportPeriodParams;

export type SetReportFrequencyAction = Action<typeof NEW_REPORT_SET_FREQUENCY> &
  SetReportFrequencyParams;

export type SetSitesAction = Action<typeof NEW_REPORT_SET_SITES> &
  SetSitesParams;

export type SetRecipientsAction = Action<typeof NEW_REPORT_SET_RECIPIENTS> &
  SetRecipientsParams;

export type ResetReportAction = Action<typeof RESET_REPORT>;

export type ResetReportFrequencyAction = Action<typeof RESET_REPORT_FREQUENCY>;

export type ResetScheduledReportsAction = Action<typeof RESET_SCHEDULED_REPORT>;

export type ClearNewReportSuccessBanner = Action<
  typeof NEW_REPORT_CLEAR_SUCCESS_BANNER
>;

export type SetCurrentPageAction = Action<
  typeof REPORTS_PAGINATION_CHANGE_PAGE
> &
  SetCurrentPageParams;

export type ApiDeleteScheduledReportAction = ApiDataAction<
  typeof API_DELETE_SCHEDULED_REPORT
> &
  DeleteScheduledReportParams;
export type ApiDeleteScheduledReportActionRequest = ApiRequestAction<
  typeof API_DELETE_SCHEDULED_REPORT_REQUEST
> &
  DeleteScheduledReportParams;
export type ApiDeleteScheduledReportActionSuccess = ApiSuccessAction<
  typeof API_DELETE_SCHEDULED_REPORT_SUCCESS
> &
  DeleteScheduledReportParams;
export type ApiDeleteScheduledReportActionFailure = ApiFailureAction<
  typeof API_DELETE_SCHEDULED_REPORT_FAILURE
> &
  DeleteScheduledReportParams;

export type DeleteScheduledReportsSetLoadingAction = Action<
  typeof DELETE_SCHEDULED_REPORT_SET_LOADING
> &
  DeleteScheduledReportParams;

export type DeleteScheduledReportsRemoveLoadingAction = Action<
  typeof DELETE_SCHEDULED_REPORT_REMOVE_LOADING
> &
  DeleteScheduledReportParams;

export type ApiSetDataFiltersAction = ApiDataAction<typeof SET_DATA_FILTERS>;

/*
  Reducer types
*/

export type ReportingPlatformActions =
  | ApiCreateReportAction
  | ApiCreateReportActionRequest
  | ApiCreateReportActionSuccess
  | ApiCreateReportActionFailure
  | ApiGetReportAction
  | ApiGetReportsAction
  | ApiGetReportsActionRequest
  | ApiGetReportsActionSuccess
  | ApiGetReportsActionFailure
  | InitialiseNewReportAction
  | SetReportPeriodAction
  | SetReportFrequencyAction
  | SetSitesAction
  | SetRecipientsAction
  | ResetReportAction
  | ResetScheduledReportsAction
  | ClearNewReportSuccessBanner
  | SetCurrentPageAction
  | ApiGetScheduledReportsAction
  | ApiGetScheduledReportsActionRequest
  | ApiGetScheduledReportsActionSuccess
  | ApiGetScheduledReportsActionFailure
  | SetScheduledReportsCurrentPageAction
  | SetScheduledReportsFilterAction
  | ApiDeleteScheduledReportAction
  | ApiDeleteScheduledReportActionRequest
  | ApiDeleteScheduledReportActionSuccess
  | ApiDeleteScheduledReportActionFailure
  | DeleteScheduledReportsSetLoadingAction
  | DeleteScheduledReportsRemoveLoadingAction
  | ApiSetDataFiltersAction
  | ResetReportFrequencyAction;
